import type { ReactNode } from 'react';
import '@fontsource/stix-two-text/latin-400.css';
import '@fontsource/stix-two-text/latin-600.css';
import '@fontsource/stix-two-text/latin-700.css';

import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  ListItem,
  Text,
  UnorderedList,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  AI_LEAGUE_METHODOLOGY_OPENING,
  AI_LEAGUE_METHODOLOGY_ORIGINAL_LIST_DIVIDER_TITLE,
  AI_LEAGUE_METHODOLOGY_PROMPT,
  AI_LEAGUE_METHODOLOGY_SECTIONS,
  AI_LEAGUE_METHODOLOGY_SWAP_PROMPT,
  AI_LEAGUE_METHODOLOGY_SWAP_SECTION,
  type AiLeagueMethodologyBlock,
  type AiLeagueMethodologySection,
} from '@/constants/aiLeague/aiLeagueMethodology';
import {
  METHODOLOGY_ARTICLE_MAX_W,
  METHODOLOGY_ARTICLE_PB,
  METHODOLOGY_ARTICLE_PT,
  METHODOLOGY_ARTICLE_PX,
  METHODOLOGY_BODY_FONT_SIZE,
  METHODOLOGY_FONT_FAMILY,
  METHODOLOGY_MAJOR_TITLE_FONT_SIZE,
  METHODOLOGY_SECTION_SPACING,
  METHODOLOGY_SECTION_TITLE_FONT_SIZE,
} from '@/constants/aiLeague/methodologyTypography';
import { AiLeagueMarkdown } from './AiLeagueMarkdown';

const METHODOLOGY_BODY_PROPS = {
  fontSize: METHODOLOGY_BODY_FONT_SIZE,
  lineHeight: '1.75' as const,
  fontFamily: METHODOLOGY_FONT_FAMILY,
  textAlign: 'left' as const,
};

const METHODOLOGY_SECTION_TITLE_PROPS = {
  fontSize: METHODOLOGY_SECTION_TITLE_FONT_SIZE,
  fontWeight: 'semibold' as const,
  fontFamily: METHODOLOGY_FONT_FAMILY,
  textAlign: 'left' as const,
  as: 'h2' as const,
};

const METHODOLOGY_MAJOR_TITLE_PROPS = {
  fontSize: METHODOLOGY_MAJOR_TITLE_FONT_SIZE,
  fontWeight: 'semibold' as const,
  fontFamily: METHODOLOGY_FONT_FAMILY,
  textAlign: 'left' as const,
  as: 'h2' as const,
};

function MethodologyInsetBlock(props: { children: ReactNode }) {
  const borderColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const bg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box
      w='full'
      borderWidth='1px'
      borderColor={borderColor}
      bg={bg}
      borderRadius='md'
      px={{ base: 4, md: 5 }}
      py={{ base: 4, md: 5 }}
      overflowX='auto'
      textAlign='left'
    >
      {props.children}
    </Box>
  );
}

function MethodologyPromptBlock(props: { content: string }) {
  return (
    <MethodologyInsetBlock>
      <AiLeagueMarkdown content={props.content} compact />
    </MethodologyInsetBlock>
  );
}

function MethodologySwapWindowSection(props: { textColor: string; dividerColor: string }) {
  const swap = AI_LEAGUE_METHODOLOGY_SWAP_SECTION;
  const ledeColor = useColorModeValue('gray.800', 'gray.100');

  return (
    <Box w='full' textAlign='left'>
      <Text {...METHODOLOGY_MAJOR_TITLE_PROPS} mb={4}>
        {swap.title}
      </Text>
      <VStack align='stretch' spacing={5}>
        <Text {...METHODOLOGY_BODY_PROPS} color={ledeColor}>
          {swap.lede}
        </Text>

        <Box w='full'>
          <Text {...METHODOLOGY_SECTION_TITLE_PROPS} mb={4}>
            {swap.promptTitle}
          </Text>
          <MethodologyPromptBlock content={AI_LEAGUE_METHODOLOGY_SWAP_PROMPT} />
        </Box>

        <Box w='full'>
          <Text {...METHODOLOGY_SECTION_TITLE_PROPS} mb={4}>
            {swap.interpretationTitle}
          </Text>
          <VStack align='stretch' spacing={4}>
            {swap.interpretation.map((block, index) => (
              <MethodologyBlock key={`interp-${index}`} block={block} textColor={props.textColor} />
            ))}
          </VStack>
        </Box>
      </VStack>

      <Divider borderColor={props.dividerColor} borderBottomWidth='2px' mt={METHODOLOGY_SECTION_SPACING} />
      <Text {...METHODOLOGY_MAJOR_TITLE_PROPS} mt={METHODOLOGY_SECTION_SPACING} mb={0}>
        {AI_LEAGUE_METHODOLOGY_ORIGINAL_LIST_DIVIDER_TITLE}
      </Text>
    </Box>
  );
}

export function AiLeagueMethodologyPanel() {
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const ledeColor = useColorModeValue('gray.800', 'gray.100');
  const dividerColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.500');
  const selectingSection = AI_LEAGUE_METHODOLOGY_SECTIONS[0];
  const sectionsAfterPrompt = AI_LEAGUE_METHODOLOGY_SECTIONS.slice(1);

  return (
    <Box
      maxW={METHODOLOGY_ARTICLE_MAX_W}
      mx='auto'
      w='full'
      px={METHODOLOGY_ARTICLE_PX}
      pt={METHODOLOGY_ARTICLE_PT}
      pb={METHODOLOGY_ARTICLE_PB}
    >
      <VStack align='stretch' spacing={METHODOLOGY_SECTION_SPACING} w='full' textAlign='left'>
        <MethodologySwapWindowSection textColor={textColor} dividerColor={dividerColor} />

        <Text {...METHODOLOGY_BODY_PROPS} color={ledeColor}>
          {AI_LEAGUE_METHODOLOGY_OPENING}
        </Text>

        <MethodologySection section={selectingSection} textColor={textColor} />

        <Box w='full'>
          <Divider borderColor={dividerColor} borderBottomWidth='2px' mb={METHODOLOGY_SECTION_SPACING} />
          <Text {...METHODOLOGY_SECTION_TITLE_PROPS} mb={4}>
            Prompt
          </Text>
          <MethodologyPromptBlock content={AI_LEAGUE_METHODOLOGY_PROMPT} />
        </Box>

        {sectionsAfterPrompt.map((section) => (
          <MethodologySection
            key={section.title}
            section={section}
            textColor={textColor}
            dividerAbove={section.title === 'Grok Struggles'}
            dividerColor={dividerColor}
          />
        ))}
      </VStack>
    </Box>
  );
}

function MethodologySection(props: {
  section: AiLeagueMethodologySection;
  textColor: string;
  dividerAbove?: boolean;
  dividerColor?: string;
}) {
  const { section, textColor, dividerAbove, dividerColor } = props;

  return (
    <Box w='full' textAlign='left'>
      {dividerAbove && dividerColor ? (
        <Divider borderColor={dividerColor} borderBottomWidth='2px' mb={METHODOLOGY_SECTION_SPACING} />
      ) : null}
      <Text {...METHODOLOGY_SECTION_TITLE_PROPS} mb={4}>
        {section.title}
      </Text>
      <VStack align='stretch' spacing={5}>
        {section.blocks.map((block, index) => (
          <MethodologyBlock key={index} block={block} textColor={textColor} />
        ))}
      </VStack>
    </Box>
  );
}

function MethodologyBlock(props: { block: AiLeagueMethodologyBlock; textColor: string }) {
  const { block, textColor } = props;

  if (block.type === 'paragraph') {
    return (
      <Text {...METHODOLOGY_BODY_PROPS} color={textColor}>
        {block.text}
      </Text>
    );
  }

  if (block.type === 'bullets') {
    return (
      <UnorderedList spacing={3} pl={5} {...METHODOLOGY_BODY_PROPS} color={textColor}>
        {block.items.map((item) => (
          <ListItem key={item} textAlign='left'>
            {item}
          </ListItem>
        ))}
      </UnorderedList>
    );
  }

  if (block.type === 'markdown') {
    return <AiLeagueMarkdown content={block.content} />;
  }

  return (
    <MethodologyCollapsibleMarkdown triggerLabel={block.triggerLabel} content={block.content} />
  );
}

function MethodologyCollapsibleMarkdown(props: { triggerLabel: string; content: string }) {
  const borderColor = useColorModeValue('blackAlpha.200', 'whiteAlpha.300');
  const bg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  const triggerColor = useColorModeValue('gray.700', 'gray.200');

  return (
    <Accordion allowToggle w='full' reduceMotion>
      <AccordionItem borderWidth='1px' borderColor={borderColor} borderRadius='md' overflow='hidden'>
        <AccordionButton
          px={{ base: 4, md: 5 }}
          py={3}
          bg={bg}
          _hover={{ bg: hoverBg }}
          _expanded={{ bg: hoverBg }}
          fontFamily={METHODOLOGY_FONT_FAMILY}
          fontSize={METHODOLOGY_BODY_FONT_SIZE}
          fontWeight='medium'
          color={triggerColor}
          textAlign='left'
        >
          <Box as='span' flex='1' textAlign='left'>
            {props.triggerLabel}
          </Box>
          <AccordionIcon as={ChevronDownIcon} />
        </AccordionButton>
        <AccordionPanel
          px={{ base: 4, md: 5 }}
          py={4}
          bg={bg}
          borderTopWidth='1px'
          borderColor={borderColor}
        >
          <AiLeagueMarkdown content={props.content} />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
